import { useMachine } from '@xstate/react';
import { useCallback, useEffect, useState } from 'react';
import { CommonsPanel } from './components/CommonsPanel';
import { AnswerPanel } from './components/AnswerPanel';
import { SceneStage } from './components/SceneStage';
import { TurnstileBox } from './components/TurnstileBox';
import { trialMachine, type TrialAnswer } from './appMachine';
import { getCommons, getConfig, getScene, submitTrial } from './lib/api';
import { deviceHash } from './lib/device';
import { recentLocalPlays, saveLocalPlay } from './lib/localDb';
import { useAppStore } from './store';
import type { Commons, Scene } from '../shared/scene';
import type { LocalPlay } from './lib/localDb';

const CONSENT_FALLBACK = 'I consent to save anonymous play data to a public aggregate blind-spot map.';
const LINKEDIN_URL = 'https://www.linkedin.com/in/mindful-abhay/';
const COFFEE_URL = 'https://buymeacoffee.com/abhaypratapsingh';

export function App() {
  const [state, send] = useMachine(trialMachine);
  const { config, commons, selfReport, localPlays, setConfig, setCommons, setSelfReport, setLocalPlays } = useAppStore();
  const [route, setRoute] = useState(() => window.location.pathname);
  const [consent, setConsent] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [submittingError, setSubmittingError] = useState<string | undefined>();

  useEffect(() => {
    const onPop = () => setRoute(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    getConfig().then(setConfig).catch(console.error);
    getCommons().then(setCommons).catch(() => undefined);
    recentLocalPlays().then(setLocalPlays).catch(() => undefined);
  }, [setConfig, setCommons, setLocalPlays]);

  const loadScene = useCallback(async () => {
    send({ type: 'START' });
    setSelfReport(undefined);
    setSubmittingError(undefined);
    setTurnstileToken('');
    setConsent(false);
    try {
      const scene = await getScene();
      send({ type: 'SCENE_READY', scene });
    } catch (error) {
      send({ type: 'FAIL', error: error instanceof Error ? error.message : String(error) });
    }
  }, [send, setSelfReport]);

  useEffect(() => {
    if (state.matches('idle')) void loadScene();
  }, [state, loadScene]);

  const navigate = (path: string) => {
    history.pushState(null, '', path);
    setRoute(path);
  };

  const scene = state.context.scene;
  const answer = state.context.answer;
  const isStats = route === '/stats';
  const isHelp = route === '/help';
  const isSettings = route === '/settings';

  return (
    <main className="appShell">
      <header className="topbar">
        <button className="brand" onClick={() => navigate('/')} aria-label="Go to workspace">
          <span className="brandMark">◉</span>
          <span><b>Plain Sight Census</b><small>inattentional blindness → public blind-spot commons</small></span>
        </button>
        <nav>
          <button onClick={() => navigate('/')} className={route === '/' ? 'active' : ''}>Play</button>
          <button onClick={() => navigate('/stats')} className={isStats ? 'active' : ''}>Commons</button>
          <button onClick={() => navigate('/help')} className={isHelp ? 'active' : ''}>Why</button>
          <button onClick={() => navigate('/settings')} className={isSettings ? 'active' : ''}>Device</button>
          <a className="navLink" href={LINKEDIN_URL} target="_blank" rel="noreferrer">LinkedIn</a>
          <a className="navLink support" href={COFFEE_URL} target="_blank" rel="noreferrer">Buy me a coffee</a>
        </nav>
      </header>

      {isStats ? <StatsView commons={commons} reload={() => getCommons(scene?.id).then(setCommons)} /> : null}
      {isHelp ? <HelpView /> : null}
      {isSettings ? <SettingsView localPlays={localPlays} /> : null}
      {!isStats && !isHelp && !isSettings ? (
        <div className="workspace">
          <section className="leftPane">
            <div className="brief card">
              <p className="eyebrow">Selective attention trial</p>
              <h1>Count one thing. See what attention edits out.</h1>
              <p>Classic inattentional blindness: when the task narrows attention, even obvious events can fail to reach awareness.</p>
              <div className="creatorLinks" aria-label="Creator links">
                <a className="support" href={COFFEE_URL} target="_blank" rel="noreferrer">Support the project</a>
                <a href={LINKEDIN_URL} target="_blank" rel="noreferrer">Connect on LinkedIn</a>
              </div>
              {scene ? (
                <div className="taskBox">
                  <span>Current task</span>
                  <strong>{scene.task.prompt}</strong>
                  <small>Load {scene.task.load}/5 · scene generated by Workers AI recipe + deterministic SVG stimulus.</small>
                </div>
              ) : null}
            </div>

            {state.matches('briefing') && scene ? (
              <div className="card actionCard">
                <h2>Rules</h2>
                <p>Keep your eyes on the moving field. Count only the named targets crossing the vertical gate left-to-right. Do not rewind; do not pre-scan.</p>
                <button className="primary" onClick={() => send({ type: 'BEGIN' })}>Begin 12-second trial</button>
              </div>
            ) : null}

            {state.matches('answering') ? <AnswerPanel onAnswer={(trialAnswer: TrialAnswer) => {
              void persistLocal(scene, trialAnswer, false);
              send({ type: 'ANSWER', answer: trialAnswer });
            }} /> : null}

            {state.matches('reveal') && scene && answer ? (
              <RevealCard scene={scene} answer={answer} />
            ) : null}

            {(state.matches('reveal') || state.matches('submitted')) && scene && answer ? (
              <section className="card submitCard">
                <h2>Contribute the blind spot</h2>
                <p className="oneLineConsent">{config?.consentLine || CONSENT_FALLBACK}</p>
                <label className="checkRow">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                  <span>{config?.consentLine || CONSENT_FALLBACK}</span>
                </label>
                <TurnstileBox siteKey={config?.turnstileSiteKey} devBypass={config?.devTurnstileBypass} onToken={setTurnstileToken} />
                {submittingError ? <p className="errorText">{submittingError}</p> : null}
                {selfReport ? <SelfReportCard /> : null}
                <div className="buttonRow">
                  <button className="primary" disabled={!consent || !turnstileToken || state.matches('submitting')} onClick={async () => {
                    if (!scene || !answer) return;
                    setSubmittingError(undefined);
                    send({ type: 'SUBMIT' });
                    try {
                      const hash = await deviceHash();
                      const result = await submitTrial({
                        sceneId: scene.id,
                        deviceHash: hash,
                        userAnswer: answer.userAnswer,
                        noticed: answer.noticed,
                        confidence: answer.confidence,
                        reactionMs: answer.reactionMs,
                        anomalyText: answer.anomalyText,
                        consent,
                        turnstileToken
                      });
                      setSelfReport(result.self);
                      setCommons(result.commons);
                      await persistLocal(scene, answer, true);
                      setLocalPlays(await recentLocalPlays());
                      send({ type: 'SUBMITTED' });
                    } catch (error) {
                      setSubmittingError(error instanceof Error ? error.message : String(error));
                      send({ type: 'FAIL', error: error instanceof Error ? error.message : String(error) });
                    }
                  }}>{state.matches('submitting') ? 'Submitting…' : 'Add to commons'}</button>
                  <button onClick={() => void loadScene()}>Play another scene</button>
                </div>
              </section>
            ) : null}

            {state.matches('error') ? (
              <div className="card errorCard"><h2>Scene failed</h2><p>{state.context.error}</p><button onClick={() => void loadScene()}>Try again</button></div>
            ) : null}
          </section>

          <section className="stagePane">
            {scene ? (
              <SceneStage
                scene={scene}
                running={state.matches('running')}
                reveal={state.matches('reveal') || state.matches('submitted')}
                onComplete={() => send({ type: 'FINISH' })}
              />
            ) : <div className="stageSkeleton">Generating a never-before-seen scene…</div>}
            <CommonsPanel commons={commons} />
          </section>
        </div>
      ) : null}
      <footer className="creatorFooter">
        Built by <a href={LINKEDIN_URL} target="_blank" rel="noreferrer">Mindful Abhay</a>. Back future attention tools at <a href={COFFEE_URL} target="_blank" rel="noreferrer">Buy Me a Coffee</a>.
      </footer>
    </main>
  );
}

async function persistLocal(scene: Scene | undefined, answer: TrialAnswer, submitted: boolean) {
  if (!scene) return;
  await saveLocalPlay({
    id: `${scene.id}:${submitted ? 'submitted' : 'local'}`,
    createdAt: new Date().toISOString(),
    sceneId: scene.id,
    worldName: scene.worldName,
    answer: scene.task.answer,
    userAnswer: answer.userAnswer,
    countError: answer.userAnswer - scene.task.answer,
    noticed: answer.noticed,
    anomalyLabel: scene.anomaly.label,
    blindspotClass: scene.anomaly.type,
    submitted
  });
}

function RevealCard({ scene, answer }: { scene: Scene; answer: TrialAnswer }) {
  const countError = answer.userAnswer - scene.task.answer;
  return (
    <section className="card revealCard">
      <p className="eyebrow">Reveal</p>
      <h2>{answer.noticed ? 'You saw the intruder.' : 'You missed something obvious.'}</h2>
      <p><b>{scene.anomaly.label}</b> crossed the scene in plain sight: {scene.anomaly.description}</p>
      <div className="scoreGrid">
        <div><span>Correct count</span><b>{scene.task.answer}</b></div>
        <div><span>Your count</span><b>{answer.userAnswer}</b></div>
        <div><span>Count error</span><b>{countError > 0 ? `+${countError}` : countError}</b></div>
        <div><span>Blind-spot class</span><b>{scene.anomaly.type}</b></div>
      </div>
      <p className="insight">{answer.noticed ? 'Your attention had spare bandwidth—or the anomaly matched your expectations enough to break through.' : 'This is not a vision failure. It is the cost of a successful attention filter.'}</p>
    </section>
  );
}

function SelfReportCard() {
  const selfReport = useAppStore((s) => s.selfReport);
  if (!selfReport) return null;
  return (
    <div className="selfReport">
      <strong>{selfReport.message}</strong>
      <small>Stored as anonymous structured cognition: class {selfReport.blindspotClass}, load {selfReport.attentionalLoad}, count error {selfReport.countError}.</small>
    </div>
  );
}

function StatsView({ commons, reload }: { commons?: Commons; reload: () => void }) {
  return (
    <div className="pageStack">
      <section className="card heroCard">
        <p className="eyebrow">Public blind-spot commons</p>
        <h1>The map is the product.</h1>
        <p>Every consenting play adds one human-verified point to a public aggregate: what kind of obvious things people miss, under what load, and in what visual neighborhood.</p>
        <button onClick={reload}>Refresh commons</button>
      </section>
      <CommonsPanel commons={commons} />
    </div>
  );
}

function HelpView() {
  return (
    <div className="pageStack">
      <section className="card heroCard">
        <p className="eyebrow">Cognitive grounding</p>
        <h1>Built on inattentional blindness.</h1>
        <p>When attention is absorbed by a demanding task, a fully visible unexpected event can fail to enter awareness. This app recreates that structure without humiliating the player: you get a reveal, a personal attention readout, and a commons contribution only with consent.</p>
      </section>
      <section className="card">
        <h2>What is harvested?</h2>
        <p>Only anonymous structured signals: scene class, target load, answer error, noticed/not-noticed, confidence, optional short description, and embeddings of scene/result descriptors. Device identity is a local random key hashed before submit.</p>
      </section>
      <section className="card">
        <h2>Why Workers AI?</h2>
        <p>Workers AI creates a fresh scene recipe and BGE embedding. D1 stores vectors as JSON. The Worker performs cosine similarity to form the neighborhood oracle—no Vectorize, no paid queues, no browser rendering.</p>
      </section>
    </div>
  );
}

function SettingsView({ localPlays }: { localPlays: LocalPlay[] }) {
  const [hash, setHash] = useState('');
  useEffect(() => { void deviceHash().then(setHash); }, []);
  return (
    <div className="pageStack">
      <section className="card heroCard">
        <p className="eyebrow">Anonymous device-keyed identity</p>
        <h1>Your identity is a hash, not an account.</h1>
        <p><code>{hash ? `${hash.slice(0, 18)}…${hash.slice(-8)}` : 'calculating…'}</code></p>
      </section>
      <section className="card">
        <h2>Local play history</h2>
        {localPlays.length ? localPlays.map((play) => (
          <div className="historyRow" key={play.id}>
            <span>{new Date(play.createdAt).toLocaleString()}</span>
            <b>{play.worldName}</b>
            <em>{play.noticed ? 'noticed' : 'missed'} · error {play.countError}</em>
          </div>
        )) : <p>No local history yet.</p>}
      </section>
    </div>
  );
}
